import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { Avatar } from '@/components/atoms/Avatar';
import { Button } from '@/components/atoms/Button';
import { FormField } from '@/components/molecules/FormField';
import { MetricCard } from '@/components/molecules/MetricCard';
import { ChangePasswordForm, CHANGE_PASSWORD_LABELS_ES } from '@/components/organisms/ChangePasswordForm';
import { ChangeEmailForm, CHANGE_EMAIL_LABELS_ES } from '@/components/organisms/ChangeEmailForm';
import { Spinner } from '@/components/atoms/Spinner';
import { Shield, Users, FileText } from 'lucide-react';
import { getBackofficeMetrics } from '@/services/api/backoffice.api';
import { usersAPI } from '@/services/api/users.api';
import type { User } from '@/types/auth.types';
import type { BackofficeMetrics } from '@/types/backoffice.types';
import styles from './ProfilePage.module.scss';

/**
 * Backoffice Admin Profile Page
 * Backoffice admin profile with settings and system information
 */
export function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [platformStats, setPlatformStats] = useState<BackofficeMetrics | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await usersAPI.updateMe({ name: formData.name });
      // Update auth context with new user data
      updateUser({ ...result.user, role: result.user.role as User['role'], level: result.user.level as User['level'] });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      // TODO: Show error toast/notification
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
    });
    setIsEditing(false);
  };

  useEffect(() => {
    const loadPlatformStats = async () => {
      try {
        setIsLoadingStats(true);
        const metrics = await getBackofficeMetrics();
        setPlatformStats(metrics);
      } catch (error) {
        console.error('Failed to load platform stats:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    void loadPlatformStats();
  }, []);

  return (
    <div className={styles.profile}>
      {/* Header */}
      <div className={styles.profile__header}>
        <h1 className={styles.profile__title}>Perfil</h1>
        <p className={styles.profile__subtitle}>Gestiona la configuración de tu cuenta de administrador</p>
      </div>

      {/* Content Grid */}
      <div className={styles.profile__content}>
        {/* Profile Info Card */}
        <Card className={styles.profile__infoCard}>
          <CardHeader>
            <CardTitle>Información personal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={styles.profile__avatarSection}>
              <Avatar src={undefined} alt={user?.name || 'Admin'} size="xl" fallback={user?.name || 'A'} />
              <div className={styles.profile__avatarInfo}>
                <p className={styles.profile__avatarName}>{user?.name}</p>
                <p className={styles.profile__avatarEmail}>{user?.email}</p>
                <p className={styles.profile__avatarRole}>Administrador de backoffice</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className={styles.profile__form}>
              <FormField
                label="Nombre completo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!isEditing}
                required
              />

              <FormField
                label="Correo electrónico"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={true}
                required
              />

              <div className={styles.profile__actions}>
                {isEditing ? (
                  <>
                    <Button type="submit" variant="primary">
                      Guardar cambios
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCancel}>
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <Button type="button" variant="primary" onClick={() => setIsEditing(true)}>
                    Editar perfil
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Change Email */}
        <Card className={styles.profile__infoCard}>
          <CardHeader>
            <CardTitle>Cambiar correo electrónico</CardTitle>
          </CardHeader>
          <CardContent>
            <ChangeEmailForm labels={CHANGE_EMAIL_LABELS_ES} />
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className={styles.profile__infoCard}>
          <CardHeader>
            <CardTitle>Cambiar contraseña</CardTitle>
          </CardHeader>
          <CardContent>
            <ChangePasswordForm labels={CHANGE_PASSWORD_LABELS_ES} />
          </CardContent>
        </Card>

        {/* System Stats */}
        <div className={styles.profile__stats}>
          <h2 className={styles.profile__statsTitle}>Resumen de la plataforma</h2>
          {isLoadingStats ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem' }}>
              <Spinner size="md" />
              <p>Cargando estadísticas de la plataforma...</p>
            </div>
          ) : platformStats ? (
            <div className={styles.profile__statsGrid}>
              <MetricCard
                icon={Shield}
                label="Rol"
                value="Administrador de backoffice"
                variant="primary"
              />
              <MetricCard
                icon={Users}
                label="Total usuarios"
                value={platformStats.totalUsers.toString()}
                variant="success"
              />
              <MetricCard
                icon={FileText}
                label="Total historias"
                value={platformStats.totalStories.toString()}
                variant="primary"
              />
            </div>
          ) : (
            <p>No se pudieron cargar las estadísticas de la plataforma.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
